package com.neurobs.ccabserver.transcriptreview.states

import com.neurobs.ccab.casr.EngineIdentifiers
import com.neurobs.ccabserver.transcriptreview.util.{NonEditableTranscript, RecordingTranscriptReviewHandler}

/** A name in a LabelList
 *
 * @param name The name
 * @param count The number of times the name should appear
 */
case class LabelListItem
(
	name: String,
	count: Int
)

/** An alphabetical list of names, each of which may appear more than once */
class LabelList (val items: Seq[LabelListItem]) {

	/** New list made from this one and another */
	def combine(rhs: LabelList): LabelList = {
		val both = items ++ rhs.items
		val grouped = both.groupBy(_.name)
		val newItems = grouped.map(g => LabelListItem(g._1, g._2.foldLeft(0)((i,c) => i.max(c.count)))).toVector
		new LabelList(newItems.sortBy(_.name))
	}

	/** The list of labels in order, possibly with multiple occurrences */
	def labels(): Seq[String] = {
		items.flatMap(i => Seq.fill(i.count)(i.name))
	}
}

object LabelList {
	def apply(names: Seq[String]): LabelList = {
		val items = names.groupBy(n => n).map(g => LabelListItem(g._1, g._2.size)).toVector
		new LabelList(items.sortBy(_.name))
	}
}

/** Manages the list of transcript labels and finds them in the right order
 * for each recording */
class TranscriptLabelList(private val reviewed: LabelList, private val asrs: LabelList) {
	def combine(rhs: TranscriptLabelList): TranscriptLabelList = {
		new TranscriptLabelList(reviewed = reviewed.combine(rhs.reviewed), asrs = asrs.combine(rhs.asrs))
	}

	/** The combined list of transcript labels for all recordings to display at the left */
	def labels(): Seq[String] =
		Seq(EngineIdentifiers.getDisplayName(EngineIdentifiers.CASR)) ++
		reviewed.labels() ++
		asrs.labels().map(EngineIdentifiers.getDisplayName)

	/** Gets the list of transcripts to display for each recording in same order as the labels */
	def transcripts(handler: RecordingTranscriptReviewHandler): Seq[Option[NonEditableTranscript]] = {
		val r = TranscriptLabelList.getTranscripts(reviewed, handler.reviewedTranscripts)
		val a = TranscriptLabelList.getTranscripts(asrs, handler.asrTranscripts)
		Seq(Some(handler.casrTranscript)) ++ r ++ a
	}
}

object TranscriptLabelList {
	private def apply(): TranscriptLabelList = new TranscriptLabelList(LabelList(Seq[String]()), LabelList(Seq[String]()))

	private def apply(handler: RecordingTranscriptReviewHandler): TranscriptLabelList = {
		val r = handler.reviewedTranscripts.map(_.engineIdentifier)
		val a = handler.asrTranscripts.map(_.engineIdentifier)

		new TranscriptLabelList(reviewed = LabelList(r), asrs = LabelList(a))
	}

	def apply(recordings: Seq[RecordingTranscriptReviewHandler]): TranscriptLabelList = {
		recordings.map(TranscriptLabelList(_)).foldLeft(TranscriptLabelList())(_.combine(_))
	}

	private def getTranscripts(list: LabelList, trans: Seq[NonEditableTranscript]): Seq[Option[NonEditableTranscript]] = {
		for {
			item <- list.items
			i <- Range(0, item.count)
		} yield {
			trans.filter(_.engineIdentifier == item.name).lift(i)
		}
	}
}